/**
 * The first vehicles (WORKBOARD E3, §10.2; ruling 24): stand-ins against the style sheet, sized
 * from the person (a person is 1.8 person units — about a metre each — scaled by personHeight / 1.8,
 * so they shrink with k_human as people do). Today's 1.1 u unit stays the giant class; these are the
 * small class the owner wants first, tracked and wheeled before walkers:
 *
 *   - **tracked** — the field carrier: a low hull on two tracks with a hexagonal turret carrying the
 *     field-class tube (the mortar: high arc, same planet). ~3 people long, a person and a half high.
 *   - **wheeled** — the transport (SCALE §5: the vehicle is also the transport): a 6×6 with a rear
 *     ramp a person ducks through, and a light gun cupola. ~3.7 people long.
 *   - **walker** — the mobility walker: two reverse-knee legs under a cockpit pod and a launcher arm,
 *     ~3.1 people tall. Its feet are planted by a two-bone leg IK on a gait cycle (no foot skate).
 *
 * Rules (§10.2): one hue in three values (unitPalette.ts) + metal; utilitarian panels and hatches,
 * not greebles (Carrier Command 2); person-scale hatches whose anchors (`crew`) are where a person's
 * feet go to stand waist-deep in them — the crew roles of core/seats.ts (gunner, driver, spotter);
 * a silhouette tier for distance. Faces +Z. Presentation only.
 *
 * Design reference: design_sheets/vehicles.html
 */
import * as THREE from 'three';
import { REFERENCE, CONVOY } from '../constants';
import { bakeStaticParts, markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import type { MountArmament } from '../types/body';
import type { PersonTier } from './personModel';
import { buildPlayHmg, hmgAmmoCan, HMG_ON_CRADLE } from './gunLooks';   // 65.36: the .50 as play draws it (D; baked, still)
import { buildMount, collapseTube, bedRack, mountInPlay, MOUNT_PICKS, type MountInPlay } from './technicalMounts';
import type { MortarRig } from './mortarLooks';   // 69.21 step 5: the launchers' shoulder yokes
import { damageModel, DAMAGE_FORM } from './damageForms';

export type VehicleKind = 'tracked' | 'wheeled' | 'walker' | 'motorcycle' | 'technical' | 'supplyTruck';
export const VEHICLE_KINDS: VehicleKind[] = ['tracked', 'wheeled', 'walker', 'motorcycle', 'technical', 'supplyTruck'];   // (166: the supply truck)
/** The motorcycle's two builds (125(d)): a loud gas engine with its exhaust, or a quiet electric pack with a hub motor. */
export type BikeVariant = 'gas' | 'electric';
export type CrewRole = 'driver' | 'gunner' | 'spotter' | 'passenger';   // (63.1o: the technical's cab passenger)

export interface VehicleModel {
  kind: VehicleKind;
  root: THREE.Group;
  full: THREE.Group;
  silhouette: THREE.Group;
  /** Rides the suspension (bob, sway); everything but the running gear hangs off it. */
  hull: THREE.Group;
  /** Yaws with the aim (absent on the walker, whose pod turns with its body). */
  turret: THREE.Group | null;
  /** Pitches with the aim. */
  cradle: THREE.Group | null;
  muzzle: THREE.Object3D | null;
  /** Where a crew member's feet go to stand in their hatch (in the hatch's own space; read with getWorldPosition). */
  crew: Partial<Record<CrewRole, THREE.Object3D>>;
  /** The transport's boarding point: the foot of the open ramp. */
  bay: THREE.Object3D | null;
  /** The motorcycle's handlebar grips (they turn with the steering), for the rider's hands; null on the others. */
  bars: { left: THREE.Object3D; right: THREE.Object3D } | null;
  /** 63.1o: the crew roles drawn hidden — seated inside a closed cab (the technical's driver and passenger) — the hosts draw nobody there. */
  hiddenCrew?: CrewRole[];
  /** 63.1: where a crew member's hands go at their post — the gunner's on the hatch gun's grips (they turn and pitch with it). */
  hands: Partial<Record<CrewRole, { left: THREE.Object3D; right: THREE.Object3D }>>;
  /** 63.1: where the bay's passengers step out with the ramp down — one a seat, fanned behind the ramp's foot (on the ground, not riding the suspension); empty without a bay. */
  bayExits: THREE.Object3D[];
  tier: PersonTier;
  /** Animation state (presentation). */
  anim: { wheels: THREE.Object3D[]; wheelRadius: number; steer: THREE.Object3D[]; tracks: TrackLoop[]; ramp: THREE.Group | null; legs: WalkerLeg[]; phase: number; t: number;
    /** 63.1e: the transport's outboard fittings (the arches' flares, stowage, foot-steps): they ride with the hull, but in their own group, so the hull's own box — the seats' entries are measured beside it — does not grow */
    fittings: THREE.Group | null;
    /** 67.60 (the D look): parts on hinges — doors, hatch lids, the tailgate, the bay's roof hatches, the bike's stand — each turned `angle` about its `axis` at full share */
    opens?: OpenPart[] };
  /** 67.60: where a damaged form's scorch goes on its flat sides, when not its kind's (`VEHICLE_FORMS.sides`): the BTR's is its lower hull, under the sloping upper sides. */
  sides?: { x: number; y: [number, number]; z: [number, number] };
  /** 67.60: its roof's height at z, when not the 6×6's (`roofAt`): where the wreck's burn lies (the BTR's roof is flat, higher). */
  roofAt?: (z: number) => number;
  /** 166: the supply truck's load (setTruckLoad): its crate rows, the top crate, the lashed tarp, the folded tarp and the loose straps, the far tier's hump; `k` the state shown. */
  cargo?: { rows: THREE.Group[]; top: THREE.Group; tarp: THREE.Group; folded: THREE.Group; hanging: THREE.Group; hump: THREE.Mesh; k: number; floor: number; crateTall: number; /** 69.34: its bed's rounds drawn at the tailgate (rendering/ammoBoxes setTruckAmmo), made the first time it holds any */ ammo?: import('./ammoBoxes').AmmoDump };
  /** 69.29: the tank's own (rendering/tankLooks.ts buildTank): the barrel (it recoils: baked on its own), the main gun's and the coax's muzzles, the commander's place at the cupola's gun; absent on the others */
  tank?: { barrel: THREE.Group; muzzle: THREE.Object3D; coaxMuzzle: THREE.Object3D; commander: { seat: THREE.Object3D; hands: { right: THREE.Object3D; left: THREE.Object3D }; muzzle: THREE.Object3D; /** 249: the cupola's ring (turns) and gun (pitches): tankLooks poseCupola */ ring?: THREE.Group; cradle?: THREE.Group }; /** a mobility kill's engine smoke (damageTank) */ smoke: THREE.Object3D[]; /** 69.31: the bustle's ammunition door (it turns: TANK_AMMO_DOOR) and where a crate is set on it open; built with `ammoDoor` */ door?: THREE.Group; loadPoint?: THREE.Object3D };
  /** 69.23: the mortar carrier's rig (rendering/mortarLooks.ts): the tube on its turntable, the hatch's leaves, the racks, the crew's anchors; posed by the host (poseMortarCrew); absent on the others */
  mortar?: MortarRig;
  /** 69.21 step 5: a launcher technical's mount in play (rendering/technicalMounts.ts): its build, the rack of spares at the front of the bed, the reload's pose (`poseMount`); absent on the .50's */
  mount?: MountInPlay;
  /** 67.77: its smoke launchers' barrels, breech and muzzle (rendering/smokeLauncher.ts fitSmokeLaunchers; core/vehicleSmoke.ts smokeMuzzles' order); absent without launchers. */
  smokeMuzzles?: Array<{ breech: THREE.Object3D; muzzle: THREE.Object3D }>;
}

/**
 * 67.60: a part on a hinge — its group turned `angle` (radians) about `axis` from `base` at full share. A `door` (the cab's),
 * a `tailgate`, a `bayHatch` (the 6×6's roof hatches) open with `VehicleMotion.doors` (shut unless given); a `hatch` (a
 * crew's lid) with `hatches` (open unless given: the crew stands in it); the bike's `stand` is down while it stands still.
 */
export interface OpenPart { group: THREE.Group; axis: 'x' | 'y' | 'z'; angle: number; base: number; kind: 'door' | 'tailgate' | 'bayHatch' | 'hatch' | 'stand'; /** a crew lid's seat (VehicleMotion.crewed) */ role?: CrewRole }

export interface VehicleMotion {
  /** Forward speed in person units per second (a run is ~5). */
  speed: number;
  /** Front-wheel steer, radians (wheeled). */
  steer?: number;
  /** Turret yaw and tube pitch, radians. */
  aimYaw?: number;
  aimPitch?: number;
  /** The transport's ramp: 0 closed … 1 down on the ground. */
  ramp?: number;
  /** The motorcycle's lean into a turn, radians: + rolls the hull toward its −x — the rider's right in the hosts' basis (+x = up × front, the left); the rider leans with it. */
  lean?: number;
  /** 67.60: the doors, the tailgate and the bay's roof hatches, 0 shut … 1 open (shut unless given). */
  doors?: number;
  /** 67.60: the crew's hatch lids, 0 shut … 1 open (open unless given). */
  hatches?: number;
  /** 67.60's switch-on: a lid's share by its crew seat (open while its seat is crewed, shut while empty); over `hatches` where given. */
  crewed?: Partial<Record<CrewRole, number>>;
}

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

const matCache = new Map<string, THREE.MeshLambertMaterial>();
export function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
export function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
export function group(parent: THREE.Object3D, x = 0, y = 0, z = 0): THREE.Group {
  const g = new THREE.Group(); g.position.set(x, y, z); parent.add(g); return g;
}
export function anchor(parent: THREE.Object3D, x: number, y: number, z: number): THREE.Object3D {
  const o = new THREE.Object3D(); o.position.set(x, y, z); parent.add(o); return o;
}
/**
 * A side profile (points as [z forward, y up]) extruded `width` across X, centred. With `bevel` (63.1e) its edges are
 * chamfered at 45° that deep, inside the same outline: the profile is inset by the bevel and the extrusion grows back
 * to it (three's bevel offsets the outline outward), so a hit shape measured from the model does not move.
 */
export function profile(parent: THREE.Object3D, pts: Array<[number, number]>, width: number, color: string, x = 0, bevel = 0): THREE.Mesh {
  const outline = bevel > 0 ? inset(pts, bevel) : pts;
  const shape = new THREE.Shape(outline.map(([z, y]) => new THREE.Vector2(z, y)));
  const geo = new THREE.ExtrudeGeometry(shape, bevel > 0
    ? { depth: width - 2 * bevel, bevelEnabled: true, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 1 }
    : { depth: width, bevelEnabled: false });
  geo.rotateY(-Math.PI / 2);          // shape x → world z; the extrusion runs along −X
  geo.translate(width / 2 - bevel + x, 0, 0);
  const m = new THREE.Mesh(geo, mat(color));
  parent.add(m);
  return m;
}
/** A polygon's outline moved `d` inward: each vertex where its two edges' inward offsets meet. */
function inset(pts: Array<[number, number]>, d: number): Array<[number, number]> {
  let area = 0;
  for (let i = 0; i < pts.length; i++) { const [a, b] = pts[i], [c, e] = pts[(i + 1) % pts.length]; area += a * e - c * b; }
  const sgn = area > 0 ? 1 : -1;      // counter-clockwise: the inside is to each edge's left
  const lines = pts.map(([a, b], i) => {
    const [c, e] = pts[(i + 1) % pts.length], l = Math.hypot(c - a, e - b), nx = -(e - b) / l * sgn, ny = (c - a) / l * sgn;
    return { px: a + nx * d, py: b + ny * d, dx: (c - a) / l, dy: (e - b) / l };
  });
  return pts.map((_, i) => {
    const A = lines[(i + pts.length - 1) % pts.length], B = lines[i];
    const den = A.dx * B.dy - A.dy * B.dx;
    const t = ((B.px - A.px) * B.dy - (B.py - A.py) * B.dx) / den;
    return [A.px + A.dx * t, A.py + A.dy * t] as [number, number];
  });
}
/** 63.1e: a part a damaged form can lose (a skirt panel, stowage, a lamp, the aerial) — `damageVehicleModel` takes a share. */
export function loose<T extends THREE.Object3D>(o: T): T { o.userData.loose = true; return o; }
/** A part lying on a slope `a` (radians, falling forward) at (z, y) on its surface, lifted `h` off it. */
export function onSlope(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number, a: number): THREE.Mesh {
  const b = box(parent, w, h, d, color, x, y + Math.cos(a) * h / 2, z + Math.sin(a) * h / 2);
  b.rotation.x = a;
  return b;
}
/** A ring sector in the side plane (an arch over a wheel at (z, y)): radii r0 … r1 over the top half. */
export function arch(z: number, y: number, r0: number, r1: number, n = 9): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i <= n; i++) { const t = Math.PI * i / n; out.push([z + r1 * Math.cos(t), y + r1 * Math.sin(t)]); }
  for (let i = n; i >= 0; i--) { const t = Math.PI * i / n; out.push([z + r0 * Math.cos(t), y + r0 * Math.sin(t)]); }
  return out;
}
/** A wheel turning about X: a tyre in the dark, a hub in metal. Returns the spinning group. */
export function wheel(parent: THREE.Object3D, r: number, w: number, tyre: string, hub: string, x: number, y: number, z: number, spokes = true): THREE.Group {
  const g = group(parent, x, y, z);
  const t = new THREE.Mesh(new THREE.CylinderGeometry(r, r, w, 12), mat(tyre)); t.rotation.z = Math.PI / 2; g.add(t);
  const h = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.55, r * 0.55, w + 0.04, 8), mat(hub)); h.rotation.z = Math.PI / 2; g.add(h);
  if (spokes) box(g, w + 0.06, r * 1.1, r * 0.18, hub, 0, 0, 0);    // a bar across the hub, so the spin reads
  return g;
}

/** A track loop round a stadium (two end circles joined top and bottom), its links one InstancedMesh. */
export interface TrackLoop { mesh: THREE.InstancedMesh; x: number; zr: number; zf: number; cy: number; r: number; length: number; pitch: number; offset: number }
function trackLoop(parent: THREE.Object3D, x: number, zr: number, zf: number, cy: number, r: number, width: number, color: string): TrackLoop {
  const length = 2 * (zf - zr) + 2 * Math.PI * r;
  const pitch = 0.25;
  const count = Math.floor(length / pitch);
  const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(width, 0.08, pitch * 0.82), mat(color), count);
  mesh.name = 'treads';
  mesh.frustumCulled = false;
  mesh.userData.noBake = true;
  parent.add(mesh);
  const loop: TrackLoop = { mesh, x, zr, zf, cy, r, length, pitch: length / count, offset: 0 };
  placeTrack(loop);
  return loop;
}
const _m4 = new THREE.Matrix4(), _q4 = new THREE.Quaternion(), _p4 = new THREE.Vector3(), _s4 = new THREE.Vector3(1, 1, 1), _x4 = new THREE.Vector3(1, 0, 0);
/** Lay the links along the loop: the bottom run goes rearward (it is still on the ground while the hull moves on). */
export function placeTrack(t: TrackLoop): void {
  const D = t.zf - t.zr, arc = Math.PI * t.r;
  for (let i = 0; i < t.mesh.count; i++) {
    let s = ((i * t.pitch + t.offset) % t.length + t.length) % t.length;
    let z: number, y: number, dz: number, dy: number;
    if (s < D) { z = t.zf - s; y = t.cy - t.r; dz = -1; dy = 0; }                                         // bottom, front → rear
    else if ((s -= D) < arc) { const a = -Math.PI / 2 - s / t.r; z = t.zr + t.r * Math.cos(a); y = t.cy + t.r * Math.sin(a); dz = Math.sin(a); dy = -Math.cos(a); }   // rear, up
    else if ((s -= arc) < D) { z = t.zr + s; y = t.cy + t.r; dz = 1; dy = 0; }                           // top, rear → front
    else { s -= D; const a = Math.PI / 2 - s / t.r; z = t.zf + t.r * Math.cos(a); y = t.cy + t.r * Math.sin(a); dz = Math.sin(a); dy = -Math.cos(a); }   // front, down
    _q4.setFromAxisAngle(_x4, Math.atan2(-dy, dz));
    _m4.compose(_p4.set(t.x, y, z), _q4, _s4);
    t.mesh.setMatrixAt(i, _m4);
  }
  t.mesh.instanceMatrix.needsUpdate = true;
}

/** A walker leg: hip → thigh (down-back to the knee) → shin (down-front to the ankle) → a flat foot. */
interface WalkerLeg { hip: THREE.Group; shin: THREE.Group; foot: THREE.Group; a: number; b: number; height: number; phase: number }

export const RAMP_DOWN = Math.PI / 2 + Math.asin(0.6 / 1.55);
/** How deep a crew member stands in a hatch: their feet this far below the rim, so they show from the chest up. */
/** How deep a crew member stands in a hatch (person units): the anchor is this far below the rim, so the rim crosses them at the waist. */
export const HATCH_DEPTH = 1.15;
/** 65.22: how much higher the cupola gunner stands behind the HMG than behind the light gun (model units: the turret's measured top with the HMG over the light gun's, 1.652 − 1.625 people). */
export const HMG_CUPOLA_STAND = 0.049;

// Walker proportions and gait (person units)
const WALKER_HIP = 4.2;   // hip height: with the pod, ~3.6 people tall
const WALKER_LEG = 2.4;   // thigh and shin
const STRIDE = 2.2;       // a step's length
const STANCE = 0.6;       // fraction of the cycle a foot is on the ground
const LIFT = 0.55;
const FOOT_SOLE = 0.18;   // ankle above the sole

// ---------------------------------------------------------------------------
// The three vehicles
// ---------------------------------------------------------------------------

/** The carrier's side profile (z forward, y up): a sloped glacis, a flat deck, a near-upright rear plate. */
export const TRACKED_HULL: Array<[number, number]> = [[-2.7, 0.45], [-2.75, 1.42], [1.55, 1.42], [2.8, 0.88], [2.62, 0.45]];
/** The glacis's fall (radians) and a point on it `t` of the way from the deck's edge to the nose. */
export const GLACIS = Math.atan2(1.42 - 0.88, 2.8 - 1.55);
export const onGlacis = (t: number): [number, number] => [1.55 + 1.25 * t, 1.42 - 0.54 * t];

function tracked(m: VehicleModel, p: UnitPalette): void {
  const { full, hull } = m;
  // running gear (not on the hull: it does not bob)
  for (const side of [-1, 1]) {
    const x = side * 1.22;
    m.anim.tracks.push(trackLoop(full, x, -2.35, 2.25, 0.46, 0.46, 0.56, p.dark));
    m.anim.wheels.push(wheel(full, 0.42, 0.42, p.metal, p.dark, x, 0.46, -2.35, false));       // drive sprocket
    m.anim.wheels.push(wheel(full, 0.38, 0.42, p.metal, p.dark, x, 0.46, 2.25, false));        // idler
    for (let i = 0; i < 5; i++) m.anim.wheels.push(wheel(full, 0.33, 0.4, p.dark, p.metal, x, 0.37, -1.6 + i * 0.8));
  }
  m.anim.wheelRadius = 0.36;
  hull.position.y = 0;
  // hull: a sloped glacis, a flat deck, its edges chamfered (63.1e; inside the same outline); fenders over the tracks
  profile(hull, TRACKED_HULL, 2.0, p.body, 0, 0.12).name = 'body';
  for (const side of [-1, 1]) {
    box(hull, 0.62, 0.08, 5.2, p.body, side * 1.22, 0.98, -0.05);
    box(hull, 0.64, 0.1, 0.2, p.flash, side * 1.22, 0.99, 2.48);                     // a flash on each fender's nose
    // 63.1e: skirts over the tracks' top run, three panels a side (a damaged form loses some); stowage bins on the rear fenders
    for (const z of [-1.55, 0, 1.55]) loose(box(hull, 0.03, 0.26, 1.5, p.body, side * 1.515, 0.81, z));
    for (const z of [-1.6, -2.25]) loose(box(hull, 0.4, 0.34, 0.6, p.dark, side * 1.25, 1.19, z));
    // the glacis: two lamps (a guard over each) and a tow shackle under the nose
    const [lz, ly] = onGlacis(0.62);
    loose(onSlope(hull, 0.2, 0.08, 0.14, p.flash, side * 0.7, ly, lz, GLACIS));
    loose(onSlope(hull, 0.26, 0.1, 0.03, p.metal, side * 0.7, ly, lz - 0.09, GLACIS));
    box(hull, 0.1, 0.14, 0.1, p.metal, side * 0.6, 0.62, 2.72);
    // the rear: a tail lamp at each corner, an exhaust louvre each side of the deck
    loose(box(hull, 0.16, 0.1, 0.03, p.flash, side * 0.78, 1.25, -2.745));
    box(hull, 0.34, 0.05, 0.26, p.dark, side * 0.55, 1.445, -1.78);
  }
  // spare track links laid across the glacis
  { const [z, y] = onGlacis(0.32); for (const x of [-0.66, -0.33, 0, 0.33, 0.66]) loose(onSlope(hull, 0.28, 0.05, 0.22, p.dark, x, y, z, GLACIS)); }
  box(hull, 1.7, 0.07, 0.6, p.dark, 0, 1.43, -2.35);                                 // engine grille
  // driver's hatch, front left, its lid swung open flat on the deck beside it (63.1e: it used to fold into the hull), a periscope ahead
  box(hull, REFERENCE.hatch, 0.06, REFERENCE.hatch, p.dark, 0.48, 1.45, 1.05);   // 63.2: the reference hatch
  box(hull, 0.58, 0.05, 0.58, p.metal, -0.15, 1.445, 1.05).name = 'lid';
  box(hull, 0.3, 0.05, 0.08, p.dark, 0.48, 1.445, 1.44);
  m.crew.driver = anchor(hull, 0.48, 1.45 - HATCH_DEPTH, 1.05);
  // turret: a low hexagon, a flash band, two hatches; the field tube on a cradle behind a mantlet
  const turret = group(hull, 0, 1.42, -0.35); m.turret = turret;
  const hex = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.1, 0.55, 6), mat(p.body)); hex.position.y = 0.28; hex.name = 'body'; turret.add(hex);
  const band = new THREE.Mesh(new THREE.CylinderGeometry(1.105, 1.105, 0.09, 6), mat(p.flash)); band.position.y = 0.06; turret.add(band);
  const cupola = new THREE.Mesh(new THREE.CylinderGeometry(0.33, 0.36, 0.24, 8), mat(p.dark)); cupola.position.set(-0.45, 0.66, -0.35); turret.add(cupola);
  for (let k = 0; k < 5; k++) { const a = -1.2 + k * 0.6; box(turret, 0.12, 0.07, 0.05, p.metal, -0.45 + Math.sin(a) * 0.35, 0.72, -0.35 + Math.cos(a) * 0.35).rotation.y = a; }   // the spotter's vision blocks, forward
  // 63.1e: each hatch's lid stands open behind its occupant (a back plate; they used to fold into the turret)
  for (const [hx, hz, hy] of [[-0.45, -0.71, 0.78], [0.45, -0.76, 0.58]]) { const lid = group(turret, hx, hy, hz); lid.rotation.x = -0.32; box(lid, 0.6, 0.56, 0.06, p.body, 0, 0.28, 0).name = 'lid'; }
  m.crew.spotter = anchor(turret, -0.45, 0.78 - HATCH_DEPTH, -0.35);
  box(turret, REFERENCE.hatch, 0.06, REFERENCE.hatch, p.dark, 0.45, 0.58, -0.45);
  m.crew.gunner = anchor(turret, 0.45, 0.58 - HATCH_DEPTH, -0.45);
  // 63.1: the gunner's hatch gun (the armed role's direct fire, beside the field tube's lobbed): a pintle post at the
  // hatch's front edge, a light gun on it, spade grips toward the gunner standing in the hatch (exposed above its rim, 124)
  box(turret, 0.06, 0.24, 0.06, p.metal, 0.45, 0.58 + 0.12, -0.12);
  const pintle = group(turret, 0.45, 0.58 + 0.27, -0.12);
  box(pintle, 0.1, 0.11, 0.42, p.metal, 0, 0, 0.04);                                // receiver
  box(pintle, 0.04, 0.04, 0.5, p.metal, 0, 0.01, 0.48);                              // barrel
  box(pintle, 0.12, 0.1, 0.14, p.dark, -0.1, -0.02, 0.06);                           // the ammunition box
  m.hands.gunner = { right: anchor(pintle, 0.07, -0.02, -0.24), left: anchor(pintle, -0.07, -0.02, -0.24) };
  // 63.1e: smoke dischargers on the two front faces (three tubes each, aimed out and up), a stowage box on each rear face, an aerial
  for (const side of [-1, 1]) {
    const front = group(turret, side * 0.45, 0.36, 0.78); front.rotation.y = side * Math.PI / 6;
    for (const t of [-0.15, 0, 0.15]) { const c = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.22, 6), mat(p.dark)); c.position.set(t, 0, 0.04); c.rotation.x = Math.PI / 2 - 0.55; front.add(loose(c)); }
    const rear = group(turret, side * 0.485, 0.3, -0.84); rear.rotation.y = -side * Math.PI / 6;
    loose(box(rear, 0.55, 0.28, 0.22, p.dark, 0, 0, 0));
  }
  box(turret, 0.1, 0.08, 0.1, p.metal, 0, 0.6, -0.8);
  const aerial = loose(box(turret, 0.025, 1.3, 0.025, p.metal, 0, 1.25, -0.8)); aerial.rotation.x = -0.12; aerial.name = 'aerial';
  const cradle = group(turret, 0, 0.62, 0.62); m.cradle = cradle;
  box(cradle, 0.5, 0.42, 0.5, p.metal, 0, 0, 0);                                     // breech block
  box(cradle, 0.62, 0.46, 0.2, p.body, 0, 0, 0.35);                                  // 63.1e: the mantlet
  const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.18, 2.1, 10), mat(p.metal)); tube.rotation.x = Math.PI / 2; tube.position.z = 1.0; cradle.add(tube);
  const evac = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.21, 0.3, 10), mat(p.metal)); evac.rotation.x = Math.PI / 2; evac.position.z = 1.15; cradle.add(evac);   // the bore evacuator
  const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.1, 10), mat(p.flash)); ring.rotation.x = Math.PI / 2; ring.position.z = 1.62; cradle.add(ring);
  box(cradle, 0.3, 0.24, 0.26, p.metal, 0, 0, 1.91);                                 // the muzzle brake
  m.muzzle = anchor(cradle, 0, 0, 2.06);
  // far tier (63.1e: the hull's wedge, the hexagon and the tube, not three boxes)
  box(m.silhouette, 3.05, 0.95, 5.3, p.dark, 0, 0.5, -0.05);
  profile(m.silhouette, TRACKED_HULL, 2.0, p.body);
  const sh = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.1, 0.6, 6), mat(p.body)); sh.position.set(0, 1.72, -0.35); m.silhouette.add(sh);
  const st = box(m.silhouette, 0.34, 0.34, 2.1, p.metal, 0, 2.6, 0.75); st.rotation.x = -0.9;
}

/** The transport's side profile (z forward, y up): a tall box with a sloped nose and a windscreen line. */
export const WHEELED_HULL: Array<[number, number]> = [[-3.25, 0.58], [-3.3, 2.2], [1.6, 2.28], [2.85, 1.55], [3.35, 1.05], [3.05, 0.58]];
/** The roof's height at `z` (it rises 0.08 toward the front). */
export const roofAt = (z: number) => 2.2 + ((z + 3.3) / 4.9) * 0.08;

function wheeled(m: VehicleModel, p: UnitPalette, cupolaGun: 'light' | 'hmg' = 'hmg'): void {
  const { full, hull } = m;
  for (const side of [-1, 1]) {
    for (const z of [2.15, 0.6, -1.55]) {
      const steer = group(full, side * 1.2, 0.56, z);
      const w = wheel(steer, 0.56, 0.44, p.dark, p.metal, 0, 0, 0);
      m.anim.wheels.push(w);
      if (z > 2) m.anim.steer.push(steer);
    }
  }
  m.anim.wheelRadius = 0.56;
  // hull: a box with a sloped nose and a windscreen line, its edges chamfered (63.1e: an armoured hull, not a crate; inside the same outline)
  profile(hull, WHEELED_HULL, 2.3, p.body, 0, 0.2).name = 'body';
  const fit = group(full); m.anim.fittings = fit;
  for (const side of [-1, 1]) {
    box(hull, 0.06, 0.32, 5.4, p.flash, side * 1.16, 1.62, -0.3);                    // the flash stripe down each side
    // 63.1e: each wheel in a dark well under a flared arch (they were flat dark boxes)
    for (const z of [2.15, 0.6, -1.55]) {
      profile(hull, arch(z, 0.56, 0, 0.64).slice(0, 10), 0.17, p.dark, side * 1.155);   // a half disc (the arc closed along its chord), out to the old arches' 1.24: the seats' entries are measured beside it
      profile(fit, arch(z, 0.56, 0.62, 0.7), 0.42, p.body, side * 1.22).name = `flare ${side} ${z}`;
    }
    for (const z of [1.0, -0.75, -1.65, -2.55]) box(hull, 0.05, 0.18, 0.4, p.dark, side * 1.16, 1.9, z);   // vision blocks: the driver's, then the bay's
    loose(box(fit, 0.25, 0.4, 0.6, p.dark, side * 1.275, 1.08, -0.47));              // a stowage box between the back wheels
    loose(box(hull, 0.16, 0.12, 0.05, p.flash, side * 0.92, 1.95, -3.3));            // a tail lamp at each rear corner
  }
  // the left rear corner's foot-steps (REFERENCE.step) and a grab handle: the climb to the roof
  for (const [y, k] of [[0.95, 0], [1.35, 1]] as const) box(fit, 0.2, 0.04, REFERENCE.step.tread, p.metal, -1.25, y, -2.75 - k * 0.02);
  box(hull, 0.04, 0.04, 0.45, p.metal, -1.08, 2.12, -2.75);
  box(hull, 1.8, 0.42, 0.06, p.dark, 0, 1.78, 2.62).rotation.x = -0.75;              // the windscreen band
  for (const x of [-0.85, 0.85]) {
    box(hull, 0.3, 0.14, 0.06, p.flash, x, 1.12, 3.33);                               // headlights
    loose(box(hull, 0.36, 0.03, 0.08, p.metal, x, 1.21, 3.29));                       // their guards
    box(hull, 0.1, 0.12, 0.12, p.metal, x * 0.82, 0.66, 3.14);                        // tow hooks
  }
  // driver's hatch, its lid swung open flat on the roof behind it; the cupola with its gun
  box(hull, REFERENCE.hatch, 0.06, REFERENCE.hatch, p.dark, -0.55, 2.29, 1.25);
  box(hull, 0.58, 0.04, 0.58, p.metal, -0.55, roofAt(0.63) + 0.02, 0.63).name = 'lid';
  m.crew.driver = anchor(hull, -0.55, 2.29 - HATCH_DEPTH, 1.25);
  // 63.1e: the bay's two roof hatches, shut (their hinges show them)
  for (const x of [-0.45, 0.45]) { box(hull, 0.6, 0.04, 0.6, p.body, x, roofAt(-2.4) + 0.02, -2.4); box(hull, 0.6, 0.03, 0.06, p.metal, x, roofAt(-2.4) + 0.045, -2.69); }
  const turret = group(hull, 0.35, 2.27, -0.3); m.turret = turret;
  const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.46, 0.3, 8), mat(p.dark)); cup.position.y = 0.15; turret.add(cup);
  m.crew.gunner = anchor(turret, 0, 0.3 - HATCH_DEPTH, 0);
  const cradle = group(turret, 0.0, 0.42, 0.3); m.cradle = cradle;
  if (cupolaGun === 'hmg') {
    // 65.22: the heavy machine gun in the cupola — the same gun the tripod mounts (hmgModel.ts), its grips a forearm in
    // front of the gunner standing in the hatch; the cupola's gun since lane F's mechanism (63.1d step 3: the seat fires
    // MOUNTED_GUNS.hmg), the turret's hit shape measured from it (hitShapeFit.ts). 'light' keeps the old light gun.
    const hmg = buildPlayHmg(p); hmg.group.position.set(0, 0, HMG_ON_CRADLE); cradle.add(hmg.group);
    // the gunner stands higher by the turret's measured top with the HMG over the light gun's (the receiver's rear sight;
    // the barrel is thin, so not measured), so it sights over the receiver as it did over the light gun — and its drawn
    // eye is the sim's (core/drive.ts: the eye clears the turret's measured top by EYE_CLEAR; tests/hmgModel.test.ts)
    m.crew.gunner.position.y += HMG_CUPOLA_STAND;
    m.muzzle = hmg.muzzle;
    m.hands.gunner = { right: hmg.gripR, left: hmg.gripL };
  } else {
    box(cradle, 0.22, 0.2, 0.5, p.metal, 0, 0, 0);
    box(cradle, 0.07, 0.07, 1.0, p.metal, 0, 0.02, 0.7);
    box(cradle, 0.56, 0.22, 0.04, p.body, 0, 0.04, 0.42);                            // 63.1e: a gun shield
    m.muzzle = anchor(cradle, 0, 0.02, 1.2);
    m.hands.gunner = { right: anchor(cradle, 0.09, -0.04, -0.3), left: anchor(cradle, -0.09, -0.04, -0.3) };   // 63.1: the cupola gunner's grips
  }
  // the rear ramp, hinged at the bottom: a door a person ducks through (1.55 high, 1.4 wide); 63.1e: ribs on its inner face (its tread, down)
  const ramp = group(hull, 0, 0.6, -3.28); m.anim.ramp = ramp;
  box(ramp, 1.4, 1.55, 0.09, p.body, 0, 0.775, 0);
  box(ramp, 1.42, 0.08, 0.1, p.flash, 0, 1.5, 0);
  for (const y of [0.25, 0.55, 0.85, 1.15]) box(ramp, 1.24, 0.04, 0.03, p.metal, 0, y, 0.06);
  box(hull, 1.5, 1.6, 0.04, p.dark, 0, 1.4, -3.27);                                  // the opening behind it
  // 63.1e: the bay seen through it with the ramp down — its floor, a bench along each side
  box(hull, 1.3, 0.04, 0.03, p.metal, 0, 0.66, -3.3);
  for (const x of [-0.6, 0.6]) box(hull, 0.2, 0.5, 0.02, p.body, x, 0.98, -3.3);
  m.bay = anchor(ramp, 0, 1.6, 0);
  // 63.1: the bay's four seats step out here with the ramp down — fanned behind its foot, a person apart
  for (const x of [-1.35, -0.45, 0.45, 1.35]) m.bayExits.push(anchor(full, x, 0, -5.4 - Math.abs(x) * 0.3));
  // far tier (63.1e: the hull's profile over three wheel pairs and the cupola, not two boxes)
  profile(m.silhouette, WHEELED_HULL, 2.3, p.body);
  for (const z of [2.15, 0.6, -1.55]) box(m.silhouette, 2.86, 1.08, 1.0, p.dark, 0, 0.54, z);
  const sc = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.46, 0.3, 8), mat(p.dark)); sc.position.set(0.35, 2.42, -0.3); m.silhouette.add(sc);
}

function walker(m: VehicleModel, p: UnitPalette): void {
  const { hull } = m;
  const H = WALKER_HIP;
  hull.position.y = H;
  // the pod (built at 1, drawn 1.4×): a cockpit with a canopy, a flash band, a hatch; a launcher arm right, a sensor pod left
  const pod = group(hull); pod.scale.setScalar(1.4);
  profile(pod, [[-0.95, 0.15], [-1.05, 1.25], [0.7, 1.4], [1.25, 0.85], [1.1, 0.15]], 1.6, p.body);
  box(pod, 1.62, 0.12, 2.05, p.flash, 0, 0.32, 0.05);
  box(pod, 1.1, 0.45, 0.06, p.dark, 0, 1.0, 1.06).rotation.x = -0.82;                // canopy
  box(pod, 0.45, 0.05, 0.45, p.dark, 0, 1.37, -0.2);                                  // hatch (0.63 across at 1.4×: a person fits)
  m.crew.driver = anchor(hull, 0, 1.37 * 1.4 - HATCH_DEPTH, -0.2 * 1.4);
  box(pod, 1.3, 0.35, 0.5, p.metal, 0, 0.0, -0.1);                                    // hip block
  const arm = group(pod, 1.05, 0.95, 0.1); m.turret = arm;
  box(arm, 0.3, 0.3, 0.4, p.metal, 0, 0, 0);
  const cradle = group(arm, 0.1, 0, 0.1); m.cradle = cradle;
  const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 1.7, 10), mat(p.metal)); tube.rotation.x = Math.PI / 2; tube.position.z = 0.75; cradle.add(tube);
  const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.19, 0.1, 10), mat(p.flash)); ring.rotation.x = Math.PI / 2; ring.position.z = 1.5; cradle.add(ring);
  m.muzzle = anchor(cradle, 0, 0, 1.6);
  box(pod, 0.35, 0.4, 0.55, p.dark, -0.98, 0.95, 0.2);                                // sensor pod
  box(pod, 0.2, 0.12, 0.04, p.flash, -0.98, 1.0, 0.48);
  // legs: thigh down-back to a reverse knee, shin down-front, a flat two-toed foot
  const L = WALKER_LEG;
  for (const side of [-1, 1]) {
    const hip = group(hull, side * 1.0, 0, 0);
    box(hip, 0.42, L, 0.5, p.body, 0, -L / 2, 0);
    box(hip, 0.46, 0.22, 0.54, p.flash, 0, -0.45, 0);                                 // a flash on each thigh
    const shin = group(hip, 0, -L, 0);
    box(shin, 0.5, 0.5, 0.5, p.metal, 0, 0, 0);                                       // knee joint
    box(shin, 0.34, L, 0.4, p.dark, 0, -L / 2, 0);
    const foot = group(shin, 0, -L, 0);
    box(foot, 0.66, FOOT_SOLE, 1.25, p.dark, 0, -FOOT_SOLE / 2, 0.24);
    box(foot, 0.2, FOOT_SOLE * 0.85, 0.45, p.dark, -0.2, -FOOT_SOLE * 0.55, 0.98);
    box(foot, 0.2, FOOT_SOLE * 0.85, 0.45, p.dark, 0.2, -FOOT_SOLE * 0.55, 0.98);
    m.anim.legs.push({ hip, shin, foot, a: L, b: L, height: H, phase: side < 0 ? 0 : 0.5 });
  }
  // far tier: the pod and two legs
  box(m.silhouette, 2.3, 1.8, 3.0, p.body, 0, H + 1.0, 0.1);
  box(m.silhouette, 0.5, H, 0.6, p.dark, -1.0, H / 2, 0);
  box(m.silhouette, 0.5, H, 0.6, p.dark, 1.0, H / 2, 0);
}

/**
 * The motorcycle (125(d)): one seat, unarmed, the first `drive` body the owner wants to ride. Two wheels a wheelbase of
 * 1.4 apart, a raked fork that turns with the bars, a tank in the team's colour, a saddle, foot pegs. Gas: an engine
 * block with cooling fins and an exhaust along the right side; electric: a battery pack with a charge light where the
 * engine would be, a hub motor on the rear wheel, no exhaust. Everything rides the hull, so the bike — and its rider —
 * lean into a turn together. The rider sits astride (`crew.driver`: where its feet would stand, under the saddle; the
 * person's saddle pose puts the hips on the seat and the boots on the pegs) with its hands on `bars`.
 */
export const BIKE = { wheelRadius: 0.33, wheelbase: 1.4, rake: 0.326, saddleY: 0.875, saddleZ: -0.25, pegY: 0.4, pegZ: -0.08, pegX: 0.17, gripX: 0.34 };
function motorcycle(m: VehicleModel, p: UnitPalette, variant: BikeVariant): void {
  const { hull } = m, B = BIKE, R = B.wheelRadius, half = B.wheelbase / 2;
  // the rear wheel, on its swingarm
  m.anim.wheels.push(wheel(hull, R, 0.13, p.dark, p.metal, 0, R, -half));
  m.anim.wheelRadius = R;
  for (const side of [-1, 1]) {
    const arm = box(hull, 0.04, 0.05, 0.68, p.metal, side * 0.09, 0.4, -0.38);
    arm.rotation.x = 0.1;
  }
  // the steering head, raked back; the fork, the front wheel and the bars turn about it
  const head = group(hull, 0, 0.92, half - 0.2);
  head.rotation.x = -B.rake;
  const steer = group(head); m.anim.steer.push(steer);
  const fork = Math.hypot(0.92 - R, 0.2);
  for (const side of [-1, 1]) box(steer, 0.04, fork, 0.045, p.metal, side * 0.085, -fork / 2, 0);
  const front = group(steer, 0, -fork, 0);
  front.rotation.x = B.rake;                                                       // the wheel's spin stays about its axle
  m.anim.wheels.push(wheel(front, R, 0.11, p.dark, p.metal, 0, 0, 0));
  box(steer, 0.17, 0.03, 0.44, p.body, 0, -fork + R + 0.07, 0.04).rotation.x = B.rake;   // front mudguard
  box(steer, 0.7, 0.03, 0.03, p.metal, 0, 0.1, -0.05);                             // the bars
  for (const side of [-1, 1]) {
    box(steer, 0.1, 0.045, 0.045, p.dark, side * 0.31, 0.1, -0.05);               // grips
    box(steer, 0.015, 0.12, 0.015, p.metal, side * 0.26, 0.17, -0.04);            // mirror stalks
    box(steer, 0.08, 0.05, 0.015, p.dark, side * 0.27, 0.23, -0.04);              //   and mirrors
  }
  box(steer, 0.16, 0.12, 0.08, p.dark, 0, 0.0, 0.1);                                // the headlight's shell
  box(steer, 0.12, 0.08, 0.02, p.flash, 0, 0.0, 0.145);                             //   and its lens
  m.bars = { left: anchor(steer, -B.gripX, 0.1, -0.05), right: anchor(steer, B.gripX, 0.1, -0.05) };
  // the frame: a spine from the head to the seat, the tank on it, the saddle, the tail
  box(hull, 0.07, 0.07, 0.95, p.metal, 0, 0.74, 0.08).rotation.x = 0.28;
  box(hull, 0.32, 0.22, 0.46, p.body, 0, 0.86, 0.2);                                // the tank
  box(hull, 0.06, 0.02, 0.4, p.flash, 0, 0.975, 0.2);                               //   its stripe
  box(hull, 0.27, 0.08, 0.56, p.dark, 0, B.saddleY - 0.04, -0.27);                 // the saddle
  box(hull, 0.2, 0.09, 0.32, p.body, 0, 0.86, -0.66);                               // the tail
  box(hull, 0.14, 0.05, 0.02, '#c84030', 0, 0.86, -0.83);                           //   the tail light
  box(hull, 0.16, 0.025, 0.4, p.body, 0, R + 0.38, -half + 0.05).rotation.x = -0.25;   // rear mudguard
  for (const side of [-1, 1]) box(hull, 0.09, 0.03, 0.05, p.metal, side * B.pegX, B.pegY, B.pegZ);   // foot pegs
  if (variant === 'gas') {
    // the engine: a block with fins between the wheels, and the exhaust down the right side to the back
    box(hull, 0.3, 0.3, 0.36, p.metal, 0, 0.48, 0.12);
    for (let i = 0; i < 4; i++) box(hull, 0.34, 0.02, 0.3, p.dark, 0, 0.6 + i * 0.045, 0.14);
    box(hull, 0.06, 0.06, 0.7, p.metal, 0.17, 0.33, -0.18).name = 'exhaust';
    box(hull, 0.1, 0.1, 0.32, p.dark, 0.18, 0.48, -0.62).rotation.x = -0.35;      // the silencer, rising to the back
  } else {
    // the pack: a battery box with a charge light, the hub motor on the rear wheel, a port cap — and no exhaust
    box(hull, 0.3, 0.3, 0.42, p.dark, 0, 0.5, 0.12).name = 'battery';
    box(hull, 0.31, 0.04, 0.2, p.flash, 0, 0.56, 0.12);                             // the charge light along it
    box(hull, 0.06, 0.06, 0.06, p.metal, 0.15, 0.62, 0.3);                          // the port cap
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.17, 10), mat(p.metal)); hub.rotation.z = Math.PI / 2; hub.position.set(0, R, -half); hull.add(hub);
  }
  // the rider's feet would stand here: under the saddle, so the saddle pose puts its hips on the seat
  m.crew.driver = anchor(hull, 0, B.saddleY - SADDLE_HIP_Y, B.saddleZ);
  // far tier: the body and the two wheels
  box(m.silhouette, 0.36, 0.5, 1.5, p.body, 0, 0.72, 0);
  box(m.silhouette, 0.14, 2 * R, 2 * R, p.dark, 0, R, -half);
  box(m.silhouette, 0.14, 2 * R, 2 * R, p.dark, 0, R, half);
}
/** The rider's hip height in its own pose (the person model's standing hip: the saddle pose keeps it). */
export const SADDLE_HIP_Y = 0.92;

/** Build a vehicle `personHeight` world units per person (1.1 classic, 0.055 at k_human = 1/20); a motorcycle is gas unless `variant` says. */
/**
 * 63.1o (the owner: "a technical, 3 seats, .50 cal, 1 shot kill from rocket") — lane F's stand-in until lane E's model: a
 * light pickup, a driver and a passenger in its cab (closed: drawn hidden) and a gunner standing in its bed at a ring mount
 * with the heavy machine gun (the cupola's gun, hmgModel.ts), turning with the gunner as the cupola does. The cab is its own
 * named group: its hit shape is the low body to the bed's rail, the cab, and the ring mount (rendering/hitShapeFit.ts), so
 * the gunner above the rail is open to fire.
 */
export const TECHNICAL = { bedFloor: 0.85, rail: 1.35, cabRoof: 1.9, front: 2.55, back: -2.45, half: 0.875, wheelR: 0.42, mountZ: -1.25 };
function technical(m: VehicleModel, p: UnitPalette): void {
  const { full, hull } = m, T = TECHNICAL;
  for (const side of [-1, 1]) {
    for (const z of [1.55, -1.25]) {
      const steer = group(full, side * 0.82, T.wheelR, z);
      const w = wheel(steer, T.wheelR, 0.3, p.dark, p.metal, 0, 0, 0);
      m.anim.wheels.push(w);
      if (z > 0) m.anim.steer.push(steer);
    }
  }
  m.anim.wheelRadius = T.wheelR;
  // the chassis under it all, the bed (its floor and walls to the rail), the hood
  box(hull, 1.6, 0.3, T.front - T.back - 0.2, p.dark, 0, 0.55, (T.front + T.back) / 2);
  box(hull, 2 * T.half, 0.08, -0.1 - T.back, p.body, 0, T.bedFloor, (T.back - 0.1) / 2);                          // the bed's floor
  for (const side of [-1, 1]) box(hull, 0.06, T.rail - T.bedFloor, -0.1 - T.back, p.body, side * (T.half - 0.03), (T.rail + T.bedFloor) / 2, (T.back - 0.1) / 2);
  box(hull, 2 * T.half, T.rail - 0.5, 0.06, p.body, 0, (T.rail + 0.5) / 2, T.back + 0.03);                       // the tailgate
  box(hull, 2 * T.half, 0.5, 1.4, p.body, 0, 0.95, 1.85);                                                        // the hood
  box(hull, 2 * T.half, 0.12, 0.08, p.flash, 0, 1.1, T.front - 0.04);                                           // the grille's band
  for (const x of [-0.6, 0.6]) box(hull, 0.26, 0.12, 0.05, p.flash, x, 0.98, T.front);                            // headlights
  for (const side of [-1, 1]) for (const z of [1.55, -1.25]) box(hull, 0.12, 0.12, 1.0, p.dark, side * (T.half + 0.02), 1.0, z);   // the arches' lips
  // the cab: its own group (the hit shape measures it apart), dark windows all round
  const cab = group(hull); cab.name = 'cab';
  box(cab, 2 * T.half, T.cabRoof - 0.5, 1.2, p.body, 0, (T.cabRoof + 0.5) / 2, 0.55);
  box(cab, 1.6, 0.4, 0.04, p.dark, 0, 1.6, 1.16);                                                                 // the windscreen
  box(cab, 1.6, 0.36, 0.04, p.dark, 0, 1.62, -0.06);                                                              // the back window
  for (const side of [-1, 1]) box(cab, 0.04, 0.36, 0.9, p.dark, side * (T.half + 0.01), 1.62, 0.55);               // the side windows
  m.crew.driver = anchor(cab, 0.45, 0.75, 0.6);   // (seated in the cab: drawn hidden; the seat's place)
  m.crew.passenger = anchor(cab, -0.45, 0.75, 0.6);
  m.hiddenCrew = ['driver', 'passenger'];
  // the ring mount in the bed: a post, the ring at the gunner's waist, the heavy gun at its chest — the gunner stands at its
  // centre and turns with it (as in the 6×6's cupola)
  const turret = group(hull, 0, T.bedFloor + 0.04, T.mountZ); m.turret = turret;
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 1.05, 6), mat(p.metal)); post.position.set(0, 0.52, 0.42); turret.add(post);
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.035, 4, 12), mat(p.metal)); ring.rotation.x = Math.PI / 2; ring.position.y = 0.95; turret.add(ring);
  m.crew.gunner = anchor(turret, 0, 0, 0);
  const cradle = group(turret, 0, 1.1, 0.3); m.cradle = cradle;
  const hmg = buildPlayHmg(p); hmg.group.position.set(0, 0, HMG_ON_CRADLE); cradle.add(hmg.group);
  m.muzzle = hmg.muzzle;
  m.hands.gunner = { right: hmg.gripR, left: hmg.gripL };
  // far tier: the body, the cab, the gun's mount
  box(m.silhouette, 2 * T.half + 0.2, T.rail, T.front - T.back, p.body, 0, T.rail / 2, (T.front + T.back) / 2);
  box(m.silhouette, 2 * T.half, T.cabRoof - T.rail, 1.2, p.body, 0, (T.cabRoof + T.rail) / 2, 0.55);
  box(m.silhouette, 0.5, 0.6, 1.2, p.dark, 0, T.bedFloor + 1.2, T.mountZ + 0.4);
}

/**
 * 63.1o, the technical's model (lane E; the owner, Oct 12: "Technical design looks good" — the default since, the stand-in
 * behind `{ technical: 'standIn' }` until lane F refits the hit shape and the seats to it): a civilian pickup pressed into
 * service, soft — no armour read. A cab with a raked windscreen (its own group, as the stand-in's), a long hood with a
 * grille and a steel bush bar, flared arches over dark wheel wells, an open bed with ribbed walls and a tailgate, a thin
 * roll bar behind the cab, a spare wheel and jerrycans, and the .50 on a pintle at the back of the bed, the gunner
 * standing behind it (turning with it, as the stand-in's ring). `TECHNICAL_MODEL` its measures (person units).
 */
export const TECHNICAL_MODEL = { bedFloor: 0.85, rail: 1.32, cabRoof: 1.86, front: 2.6, back: -2.45, half: 0.86, wheelR: 0.42, mountZ: -1.75, cabBack: -0.05, roofFront: 0.7, screenBase: 1.22, hoodTop: 1.2, hoodFront: 1.1, sill: 0.55 };
function technicalModel(m: VehicleModel, p: UnitPalette, armament?: MountArmament): void {
  pickupBody(m, p);
  const { hull } = m, T = TECHNICAL_MODEL, fit = m.anim.fittings!;
  technicalBed(m, p, hull, fit, T, armament);
}
/** 166: the pickup both the technical and the supply truck are — wheels, chassis, hood, arches, bush bar, the closed cab (its driver and passenger hidden), the bed and its tailgate. */
function pickupBody(m: VehicleModel, p: UnitPalette): void {
  const { full, hull } = m, T = TECHNICAL_MODEL;
  for (const side of [-1, 1]) {
    for (const z of [1.6, -1.3]) {
      const steer = group(full, side * 0.8, T.wheelR, z);
      const w = wheel(steer, T.wheelR, 0.28, p.dark, p.metal, 0, 0, 0);
      m.anim.wheels.push(w);
      if (z > 0) m.anim.steer.push(steer);
    }
  }
  m.anim.wheelRadius = T.wheelR;
  const fit = group(full); m.anim.fittings = fit;
  // the chassis rails under it all; the hood (a profile falling a little to the nose); each wheel in a dark well under a flared arch
  for (const x of [-0.42, 0.42]) box(hull, 0.12, 0.16, T.front - T.back - 0.3, p.dark, x, 0.46, (T.front + T.back) / 2);
  profile(hull, [[T.screenBase, T.sill], [T.front, T.sill], [T.front, 0.98], [T.front - 0.15, T.hoodFront], [T.screenBase, T.hoodTop]], 2 * T.half, p.body, 0, 0.05).name = 'hood';
  for (const side of [-1, 1]) for (const z of [1.6, -1.3]) {
    profile(hull, arch(z, T.wheelR, 0, 0.5).slice(0, 10), 0.14, p.dark, side * (T.half - 0.06));
    profile(fit, arch(z, T.wheelR, 0.48, 0.56), 0.24, p.body, side * (T.half + 0.04)).name = `flare ${side} ${z}`;
  }
  // the grille (dark, its slats in metal), the team's band over it, the lamps; the steel bush bar out in front (loose: a damaged form drops it)
  box(hull, 1.2, 0.32, 0.04, p.dark, 0, 0.8, T.front + 0.01);
  for (let i = -3; i <= 3; i++) box(hull, 0.04, 0.28, 0.03, p.metal, i * 0.16, 0.8, T.front + 0.03);
  box(hull, 1.3, 0.05, 0.04, p.flash, 0, 0.99, T.front + 0.01);
  for (const x of [-0.66, 0.66]) box(hull, 0.24, 0.14, 0.05, p.flash, x, 0.86, T.front + 0.01);
  const bar = loose(group(fit));
  for (const y of [0.62, 1.02]) box(bar, 1.7, 0.06, 0.06, p.metal, 0, y, T.front + 0.16);
  for (const x of [-0.78, -0.3, 0.3, 0.78]) box(bar, 0.06, 0.46, 0.06, p.metal, x, 0.82, T.front + 0.16);
  // the cab: its own group (the hit shape measures it apart, as the stand-in's), a raked windscreen, dark glass all round, door seams, mirrors
  const cab = group(hull); cab.name = 'cab';
  profile(cab, [[T.cabBack, T.sill], [T.screenBase, T.sill], [T.screenBase, T.hoodTop], [T.roofFront, T.cabRoof], [T.cabBack, T.cabRoof]], 2 * T.half, p.body, 0, 0.06);
  const rake = Math.atan2(T.screenBase - T.roofFront, T.cabRoof - T.hoodTop);
  const screen = box(cab, 1.5, Math.hypot(T.screenBase - T.roofFront, T.cabRoof - T.hoodTop) - 0.12, 0.03, p.dark, 0, (T.cabRoof + T.hoodTop) / 2, (T.roofFront + T.screenBase) / 2 + 0.02);
  screen.rotation.x = -rake;   // (its top back toward the cab)
  box(cab, 1.5, 0.34, 0.03, p.dark, 0, 1.6, T.cabBack - 0.01);                                                 // the back window
  for (const side of [-1, 1]) {
    box(cab, 0.03, 0.34, 0.62, p.dark, side * (T.half + 0.005), 1.6, 0.3);                                     // the side windows
    box(cab, 0.03, 0.82, 0.03, p.dark, side * (T.half + 0.005), 1.0, 0.72);                                    // the doors' seams
    box(cab, 0.03, 0.82, 0.03, p.dark, side * (T.half + 0.005), 1.0, -0.02);
    box(cab, 0.03, 0.03, 0.14, p.metal, side * (T.half + 0.005), 1.12, 0.45);                                  // the door handles
    const mirror = loose(group(fit, side * (T.half + 0.1), 1.42, 0.72));
    box(mirror, 0.14, 0.03, 0.03, p.metal, -side * 0.06, 0, 0); box(mirror, 0.05, 0.16, 0.1, p.dark, side * 0.02, 0.04, 0);
  }
  m.crew.driver = anchor(cab, 0.45, 0.75, 0.55);   // (seated in the cab: drawn hidden; the seat's place)
  m.crew.passenger = anchor(cab, -0.45, 0.75, 0.55);
  m.hiddenCrew = ['driver', 'passenger'];
  // the bed: its floor, ribbed walls to the rail (a metal cap along it), the tailgate, the rear lamps and bumper
  const bedLen = T.cabBack - 0.05 - T.back;
  box(hull, 2 * T.half, 0.08, bedLen, p.body, 0, T.bedFloor, T.back + bedLen / 2);
  for (const side of [-1, 1]) {
    box(hull, 0.05, T.rail - T.bedFloor, bedLen, p.body, side * (T.half - 0.025), (T.rail + T.bedFloor) / 2, T.back + bedLen / 2);
    box(hull, 0.08, 0.04, bedLen, p.metal, side * (T.half - 0.025), T.rail + 0.02, T.back + bedLen / 2);
    for (let i = 0; i < 4; i++) box(hull, 0.07, T.rail - T.bedFloor - 0.08, 0.05, p.dark, side * (T.half + 0.005), (T.rail + T.bedFloor) / 2, T.back + 0.35 + i * (bedLen - 0.7) / 3);
    loose(box(hull, 0.18, 0.1, 0.04, p.flash, side * (T.half - 0.16), 1.12, T.back - 0.02));                   // a rear lamp each side
  }
  box(hull, 2 * T.half, T.rail - T.sill, 0.06, p.body, 0, (T.rail + T.sill) / 2, T.back + 0.03);              // the tailgate
  box(hull, 1.5, 0.06, 0.04, p.dark, 0, 1.05, T.back - 0.005);                                                 // its latch line
  box(hull, 2 * T.half - 0.1, 0.12, 0.14, p.metal, 0, 0.5, T.back - 0.06);                                     // the step bumper
}
/** The technical's own: the roll bar, the spare and the cans against the cab, the whip aerial, the .50 on its pintle, and its far tier. */
export function technicalBed(m: VehicleModel, p: UnitPalette, hull: THREE.Group, fit: THREE.Group, T: typeof TECHNICAL_MODEL, armament?: MountArmament): void {
  // a roll bar behind the cab (thin: no target), a spare wheel and jerrycans against the cab's back wall, a whip aerial
  const roll = group(fit), launcher = armament === 'rocket' || armament === 'missile';
  // (its hoop at the cab roof's height, under the .50's line as it swings forward over the cab)
  for (const x of [-0.74, 0.74]) { const post = box(roll, 0.07, T.cabRoof - T.rail, 0.07, p.metal, x, (T.cabRoof + T.rail) / 2, T.cabBack - 0.18); post.userData.thin = true; }
  const top = box(roll, 1.55, 0.07, 0.07, p.metal, 0, T.cabRoof - 0.035, T.cabBack - 0.18); top.userData.thin = true;
  // (69.21 step 5: a launcher's spares take the front of the bed — the rack, below — in the spare wheel's and the cans' place)
  if (!launcher) {
    const spare = loose(wheel(fit, 0.36, 0.22, p.dark, p.metal, -0.48, T.bedFloor + 0.37, T.cabBack - 0.24, false)); spare.rotation.y = Math.PI / 2;
    for (const x of [0.42, 0.62]) loose(box(fit, 0.16, 0.36, 0.26, p.body, x, T.bedFloor + 0.22, T.cabBack - 0.22));
  }
  const aerial = loose(box(fit, 0.02, 1.0, 0.02, p.dark, -T.half + 0.12, T.cabRoof + 0.5, T.cabBack + 0.08)); aerial.userData.thin = true;
  // the pintle at the back of the bed: a base plate, the post, the .50 on its cradle with an ammunition can; the gunner stands behind
  // it, between it and the tailgate, and turns with it (as the stand-in's ring)
  const turret = group(hull, 0, T.bedFloor + 0.04, T.mountZ); m.turret = turret;
  if (launcher) {
    // 69.21 step 5 (DECISIONS 241, the owner's picks: "Ok laws yoke a, ADM yoke B, MLRS A.  Ideally spare reloads are stashed in
    // the front of the bed"): the launcher on its shoulder yoke — the twin LAWs over both shoulders on the open fork, the
    // anti-dropship missile over the right one on the gooseneck — the gunner's feet the slot's origin, the tubes each one draw
    // (they move through a reload); the spares in a rack at the front of the bed, the store's count drawn
    m.crew.gunner = anchor(turret, 0, 0, 0);
    const pick = MOUNT_PICKS[armament], b = buildMount(turret, p, pick.weapon, pick);
    for (const r of b.rounds) collapseTube(r.tube!);
    const rack = bedRack(fit, p, pick.weapon as 'law2' | 'adm', { floor: T.bedFloor + 0.02, front: T.cabBack - 0.06, half: T.half });
    m.cradle = b.cradle;
    m.muzzle = b.rounds[0].muzzle;
    m.mount = mountInPlay(b, rack, m.root);
    m.hands.gunner = m.mount.hands;
    box(m.silhouette, 0.5, 0.5, 1.2, p.dark, 0, T.bedFloor + 1.4, T.mountZ + 0.3);   // far tier: the launcher at the shoulders
    box(m.silhouette, 2 * T.half + 0.12, T.rail, T.front - T.back, p.body, 0, T.rail / 2, (T.front + T.back) / 2);
    box(m.silhouette, 2 * T.half, T.cabRoof - T.rail, T.screenBase - T.cabBack, p.body, 0, (T.cabRoof + T.rail) / 2, (T.screenBase + T.cabBack) / 2);
    return;
  }
  box(turret, 0.36, 0.04, 0.36, p.metal, 0, 0.02, 0.42);
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 1.05, 8), mat(p.metal)); post.position.set(0, 0.54, 0.42); post.userData.thin = true; turret.add(post);
  m.crew.gunner = anchor(turret, 0, 0, 0);
  const cradle = group(turret, 0, 1.1, 0.3); m.cradle = cradle;
  const hmg = buildPlayHmg(p); hmg.group.position.set(0, 0, HMG_ON_CRADLE); cradle.add(hmg.group);
  hmgAmmoCan(cradle, p);                                                                                         // the ammunition can
  m.muzzle = hmg.muzzle;
  m.hands.gunner = { right: hmg.gripR, left: hmg.gripL };
  // far tier: the body to the rail, the cab, the gun on its post
  box(m.silhouette, 2 * T.half + 0.12, T.rail, T.front - T.back, p.body, 0, T.rail / 2, (T.front + T.back) / 2);
  box(m.silhouette, 2 * T.half, T.cabRoof - T.rail, T.screenBase - T.cabBack, p.body, 0, (T.cabRoof + T.rail) / 2, (T.screenBase + T.cabBack) / 2);
  box(m.silhouette, 0.5, 0.5, 1.2, p.dark, 0, T.bedFloor + 1.2, T.mountZ + 0.6);
}

/**
 * 166, the supply truck (lane E; DECISIONS 166, the owner: "just a pickup with a driver and passenger seat"): the
 * technical's pickup without its gun, the bed laden for a supply run — crates in two rows under a lashed canvas tarp humped
 * over the rail, three straps across it hooked to the rail, the last row of crates bare at the tailgate (slatted, the team's
 * stencil on their backs). From the diorama it reads as cargo, not seats: a khaki hump over the bed, banded dark. Its
 * driver and passenger sit in the cab (hidden). `SUPPLY_TRUCK_MODEL` the load's measures (person units).
 */
export const SUPPLY_TRUCK_MODEL = { loadTop: 1.76, crate: [0.74, 0.44, 0.7] as const, rows: 3, canvas: '#7f7653', wood: '#8d6c40', strap: '#2b2622' };
function supplyTruck(m: VehicleModel, p: UnitPalette): void {
  pickupBody(m, p);
  supplyLoad(m, p);
}
/** 166: the supply truck's load on its pickup (today's or 67.60's D): the crates, the tarp, the straps, the far tier's hump. */
export function supplyLoad(m: VehicleModel, p: UnitPalette): void {
  const { hull } = m, T = TECHNICAL_MODEL, L = SUPPLY_TRUCK_MODEL, [cw, ch, cl] = L.crate;
  const load = group(hull); load.name = 'load';
  const rowZ = (i: number) => T.cabBack - 0.12 - cl / 2 - i * (cl + 0.04), floor = T.bedFloor + 0.04;
  // the crates: two across, a row group each of the three along (a partial load shows the front ones); under the tarp a big
  // one across the top of the front two rows
  const rows = Array.from({ length: L.rows }, (_, i) => { const g = group(load); g.name = `crates ${i}`; return g; });
  rows.forEach((g, i) => { for (const x of [-0.39, 0.39]) box(g, cw, ch, cl, L.wood, x, floor + ch / 2, rowZ(i)); });
  const top = group(load); top.name = 'crates top';
  box(top, 2 * cw + 0.02, L.loadTop - floor - ch - 0.02, 2 * cl + 0.04, L.wood, 0, (floor + ch + L.loadTop - 0.02) / 2, (rowZ(0) + rowZ(1)) / 2);
  // the bare row at the tailgate: slats, and the team's stencil on each crate's back (one stacked on, loose: a damaged form loses it)
  const bare = rowZ(L.rows - 1), last = rows[L.rows - 1];
  for (const x of [-0.39, 0.39]) {
    for (const y of [floor + 0.12, floor + 0.32]) box(last, cw + 0.01, 0.03, cl + 0.01, p.dark, x, y, bare);
    box(last, 0.3, 0.12, 0.02, p.flash, x, floor + ch / 2, bare - cl / 2 - 0.005);
  }
  const stacked = loose(box(last, cw - 0.12, 0.34, cl - 0.12, L.wood, 0.3, floor + ch + 0.17, bare)); stacked.rotation.y = 0.18;
  // laden: the tarp over the front two rows — its top, its sides falling to the rail, a roll of it at its back edge, lashed
  // with three straps to the hooks on the rail (the hooks are the truck's: they stay)
  const z0 = T.cabBack - 0.08, z1 = rowZ(1) - cl / 2 - 0.04, len = z0 - z1, zc = (z0 + z1) / 2, edge = 0.8;
  const tarp = group(load); tarp.name = 'tarp laden';
  box(tarp, 2 * edge, 0.05, len, L.canvas, 0, L.loadTop + 0.025, zc).name = 'tarp';
  const fall = Math.atan2(T.half + 0.03 - edge, L.loadTop - T.rail), slant = Math.hypot(T.half + 0.03 - edge, L.loadTop - T.rail);
  for (const side of [-1, 1]) {
    const flank = box(tarp, 0.04, slant + 0.06, len, L.canvas, side * (edge + T.half + 0.03) / 2, (L.loadTop + T.rail) / 2, zc);
    flank.rotation.z = side * fall;
  }
  const roll = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 2 * edge + 0.04, 8), mat(L.canvas));
  roll.rotation.z = Math.PI / 2; roll.position.set(0, L.loadTop - 0.02, z1); tarp.add(roll);
  box(tarp, 2 * edge, L.loadTop - T.rail, 0.04, L.canvas, 0, (L.loadTop + T.rail) / 2, z0 + 0.01);   // its front, against the cab
  const strapZ = [0, 1, 2].map(i => z0 - len * (0.18 + 0.32 * i));
  for (const z of strapZ) {
    box(tarp, 2 * edge + 0.02, 0.025, 0.08, L.strap, 0, L.loadTop + 0.06, z);
    for (const side of [-1, 1]) {
      const band = box(tarp, 0.025, slant + 0.08, 0.08, L.strap, side * (edge + T.half + 0.03) / 2 + side * 0.025, (L.loadTop + T.rail) / 2, z);
      band.rotation.z = side * fall;
      box(hull, 0.06, 0.08, 0.1, p.metal, side * (T.half + 0.02), T.rail - 0.02, z);   // its hook on the rail
    }
  }
  // not laden: the tarp folded in a bundle across the bed against the cab (on the front crates, if any), the straps loose —
  // hanging from their hooks down the bed's sides
  const folded = group(load); folded.name = 'tarp folded';
  const bundle = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 2 * edge - 0.1, 8), mat(L.canvas));
  bundle.rotation.z = Math.PI / 2; bundle.scale.set(1, 1, 0.7); bundle.position.set(0, floor + 0.12, T.cabBack - 0.3); folded.add(bundle);
  for (const x of [-0.5, 0.1, 0.55]) box(folded, 0.05, 0.25, 0.36, L.strap, x, floor + 0.12, T.cabBack - 0.3);   // its ties
  const hanging = group(load); hanging.name = 'straps loose';
  for (const z of strapZ) for (const side of [-1, 1]) {
    const tail = box(hanging, 0.025, 0.42, 0.08, L.strap, side * (T.half + 0.05), T.rail - 0.25, z + 0.06 * side);
    tail.rotation.x = 0.12 * side;
  }
  // far tier: the body to the rail, the cab, the load's hump in canvas (its own: setTruckLoad shows it with the load)
  box(m.silhouette, 2 * T.half + 0.12, T.rail, T.front - T.back, p.body, 0, T.rail / 2, (T.front + T.back) / 2);
  box(m.silhouette, 2 * T.half, T.cabRoof - T.rail, T.screenBase - T.cabBack, p.body, 0, (T.cabRoof + T.rail) / 2, (T.screenBase + T.cabBack) / 2);
  const hump = box(m.silhouette, 2 * T.half, L.loadTop - T.rail, z0 - (bare - cl / 2), L.canvas, 0, (L.loadTop + T.rail) / 2, (z0 + bare - cl / 2) / 2);
  hump.userData.noBake = true;
  m.cargo = { rows, top, tarp, folded, hanging, hump, k: -1, floor, crateTall: ch };
  setTruckLoad(m, 1);
}

/** 166: a truck's cargo (lane A's `StructureState.cargo`) as the share of a full load (CONVOY.load); none, empty. */
export function truckLoadShare(cargo: { bandages: number; magazines: number } | undefined): number {
  if (!cargo) return 0;
  const full = CONVOY.load.bandages + CONVOY.load.magazines;
  return full > 0 ? Math.max(0, Math.min(1, (cargo.bandages + cargo.magazines) / full)) : 0;
}

/**
 * 166, the supply truck's load on the model (lane E; lane A's convoys, `StructureState.cargo`: a FOB crate's worth or
 * none): `k` the share of a full load. **Laden** (k ≥ 0.99): the crates under the lashed tarp (today's look). **Part**
 * (0 < k < 0.99): the tarp folded back in a bundle onto the front crates, the straps loose, the crate rows from the cab back
 * to the share. **Empty** (k ≤ 0): a bare bed, the tarp bundled against the cab, the straps hanging loose down its sides.
 * The far tier's hump shows with a load. A few visibility sets when the state changes; nothing otherwise.
 */
export function setTruckLoad(m: VehicleModel, k: number): void {
  const c = m.cargo;
  if (!c) return;
  const state = k >= 0.99 ? 1 : k <= 0 ? 0 : Math.max(1, Math.min(c.rows.length - 1, Math.round(k * c.rows.length))) / c.rows.length;
  if (state === c.k) return;
  c.k = state;
  const laden = state === 1, shown = laden ? c.rows.length : Math.round(state * c.rows.length);
  c.rows.forEach((g, i) => { g.visible = i < shown && !(i === c.rows.length - 1 && c.ammo?.group.visible); });   // (69.34: the tailgate's row the rounds' while it holds any)
  c.top.visible = laden; c.tarp.visible = laden;
  c.folded.visible = !laden; c.hanging.visible = !laden;
  c.folded.position.y = shown > 0 && !laden ? c.crateTall : 0;   // (on the front crates)
  c.hump.visible = shown > 0;
  c.hump.scale.y = laden ? 1 : 0.55; c.hump.position.y = laden ? 0 : -0.2;
}

/** An empty vehicle `personHeight` world units per person: its root, its full and far tiers, its hull (67.60: the D look starts from it too). */
export function vehicleShell(kind: VehicleKind, personHeight: number): VehicleModel {
  const root = new THREE.Group();
  root.scale.setScalar(personHeight / 1.8);
  const full = group(root);
  const silhouette = group(root);
  const hull = group(full);
  return {
    kind, root, full, silhouette, hull, turret: null, cradle: null, muzzle: null, crew: {}, bay: null, bars: null, hands: {}, bayExits: [], tier: 'full',
    anim: { wheels: [], wheelRadius: 0.5, steer: [], tracks: [], ramp: null, legs: [], phase: 0, t: 0, fittings: null },
  };
}

export function buildVehicleModel(kind: VehicleKind, p: UnitPalette, personHeight: number, variant: BikeVariant = 'gas', opts: { cupolaGun?: 'light' | 'hmg'; technical?: 'standIn' | 'model'; armament?: MountArmament } = {}): VehicleModel {
  const m = vehicleShell(kind, personHeight), silhouette = m.silhouette;
  if (kind === 'tracked') tracked(m, p);
  else if (kind === 'wheeled') wheeled(m, p, opts.cupolaGun);
  else if (kind === 'motorcycle') motorcycle(m, p, variant);
  else if (kind === 'technical') { if (opts.technical === 'standIn') technical(m, p); else technicalModel(m, p, opts.armament); }
  else if (kind === 'supplyTruck') supplyTruck(m, p);   // (166)   // (63.1o: lane E's model, the owner: "Technical design looks good"; the stand-in kept behind the option a while)
  else walker(m, p);
  silhouette.visible = false;
  updateVehicleModel(m, { speed: 0, aimPitch: kind === 'tracked' ? 0.9 : 0.15 }, 0);
  return m;
}

// ---------------------------------------------------------------------------
// Animation
// ---------------------------------------------------------------------------


/** Solve one walker leg so its foot lands at (fz, fy) relative to the hip: knee behind, foot flat. */
function solveLeg(leg: WalkerLeg, fz: number, fy: number): void {
  const { a, b } = leg;
  const d = Math.min(a + b - 1e-4, Math.max(Math.abs(a - b) + 1e-4, Math.hypot(fz, fy)));
  const toFoot = Math.atan2(fz, -fy);                                // angle of hip → foot from straight down, + forward
  const atHip = Math.acos(Math.min(1, Math.max(-1, (a * a + d * d - b * b) / (2 * a * d))));
  const t1 = toFoot - atHip;                                          // the thigh swings back: the knee sits behind
  const kz = a * Math.sin(t1), ky = -a * Math.cos(t1);
  const t2 = Math.atan2(fz - kz, -(fy - ky));
  leg.hip.rotation.x = -t1;
  leg.shin.rotation.x = -(t2 - t1);
  leg.foot.rotation.x = t2;                                           // undo the shin: the foot stays flat
}

/** Advance the running gear, the gait and the aim by `dt` at `motion`. Deterministic in its inputs. */
export function updateVehicleModel(m: VehicleModel, motion: VehicleMotion, dt: number): void {
  const a = m.anim;
  const v = motion.speed;
  a.t += dt;
  for (const w of a.wheels) w.rotation.x += (v * dt) / a.wheelRadius;
  for (const s of a.steer) s.rotation.y = motion.steer ?? 0;
  for (const t of a.tracks) { t.offset += v * dt; placeTrack(t); }
  // down = past level until its lip meets the ground: hinged 0.6 up, 1.55 long → π/2 + asin(0.6 / 1.55)
  if (a.ramp) a.ramp.rotation.x = -Math.min(1, Math.max(0, motion.ramp ?? 0)) * RAMP_DOWN;
  if (m.turret && m.kind !== 'walker') m.turret.rotation.y = motion.aimYaw ?? 0;
  if (m.cradle) m.cradle.rotation.x = -(motion.aimPitch ?? 0);
  if (m.kind === 'walker') {
    // the gait: each foot plants for STANCE of the cycle and swings back to the front for the rest
    // below walking speed the steps shorten at a steady cadence, so a planted foot never slides
    const amount = Math.min(1, Math.abs(v) / 3);
    if (amount > 1e-3) a.phase = (a.phase + (dt * Math.abs(v) * STANCE) / (STRIDE * amount)) % 1;
    // the pod bobs (lowest when a foot passes under it); the feet solve against it
    const hipY = a.legs[0].height - 0.08 * amount * Math.cos(4 * Math.PI * a.phase);
    m.hull.position.y = hipY;
    for (const leg of a.legs) {
      const p = (a.phase + leg.phase) % 1;
      let fz: number, lift = 0;
      if (p < STANCE) fz = STRIDE / 2 - STRIDE * (p / STANCE);
      else { const u = (p - STANCE) / (1 - STANCE); fz = -STRIDE / 2 + STRIDE * u; lift = LIFT * Math.sin(Math.PI * u); }
      fz *= amount * Math.sign(v || 1); lift *= amount;
      solveLeg(leg, fz, -hipY + FOOT_SOLE + lift);          // the ankle sits a sole's depth above the ground
    }
  } else if (m.kind === 'motorcycle') {
    m.hull.rotation.z = motion.lean ?? 0;   // into the turn, rider and all (the wheels too: they ride the hull)
  } else {
    // a little suspension: the hull rides up and down with speed
    m.hull.position.y = 0.025 * Math.min(1, Math.abs(v) / 4) * Math.sin(a.t * 9);
    m.hull.rotation.x = -0.01 * Math.min(1, Math.abs(v) / 4) * Math.sin(a.t * 6.3);
  }
  if (a.fittings) { a.fittings.position.copy(m.hull.position); a.fittings.rotation.copy(m.hull.rotation); }
  // 67.60: the hinged parts — doors and the like shut, the crew's lids open, the bike's stand down while it stands still
  if (a.opens) for (const o of a.opens) {
    const k = o.kind === 'hatch' ? (o.role && motion.crewed?.[o.role] !== undefined ? motion.crewed[o.role]! : motion.hatches ?? 1) : o.kind === 'stand' ? (Math.abs(v) < 0.2 ? 1 : 0) : (motion.doors ?? 0);
    o.group.rotation[o.axis] = o.base + o.angle * Math.min(1, Math.max(0, k));
  }
}

/**
 * Fold static parts per moving group: the hull, the turret, the cradle, the ramp, each wheel and leg piece. Track links
 * stay instanced. 67.60's switch-on: `hinges` 'live' (the sheet; the vehicle the player is in or beside) bakes each hinged
 * part on its own (it turns: a draw each); 'rest' bakes them into their parent as they stand now (pose them first:
 * `updateVehicleModel`), so the D look draws as many as today's — they no longer turn.
 */
export function bakeVehicleModel(m: VehicleModel, opts: { hinges?: 'live' | 'rest' } = {}): number {
  if (opts.hinges === 'rest') m.anim.opens = [];   // (posed and folded into their parents below: nothing to turn)
  const moving: THREE.Object3D[] = [m.hull, ...(m.cargo ? [...m.cargo.rows, m.cargo.top, m.cargo.tarp, m.cargo.folded, m.cargo.hanging] : []), ...(m.turret ? [m.turret] : []), ...(m.cradle ? [m.cradle] : []), ...(m.anim.ramp ? [m.anim.ramp] : []), ...(m.anim.fittings ? [m.anim.fittings] : []),
    ...(m.anim.opens ?? []).map(o => o.group),   // 67.60: each hinged part bakes on its own (it turns)
    ...(m.tank ? [m.tank.barrel, ...(m.tank.commander.ring && m.tank.commander.cradle ? [m.tank.commander.ring, m.tank.commander.cradle] : []), ...(m.tank.door ? [m.tank.door] : [])] : []),   // 69.29: the tank's barrel recoils; 249: the cupola's ring and gun lay; 69.31: its ammunition door opens
    ...(m.mortar ? [m.mortar.mount, m.mortar.tube, m.mortar.barrel, m.mortar.round, ...m.mortar.leaves.map(l => l.hinge), ...m.mortar.legs.map(l => l.bar)] : []),   // 69.23: the mortar's rig turns, pitches, recoils and opens
    ...m.anim.wheels, ...m.anim.steer, ...m.anim.legs.flatMap(l => [l.hip, l.shin, l.foot])];
  const isUnder = (o: THREE.Object3D, r: THREE.Object3D) => { for (let q = o.parent; q; q = q.parent) if (q === r) return true; return false; };
  let n = 0;
  for (const g of moving) n += bakeStaticParts(g, { exclude: moving.filter(o => o !== g && isUnder(o, g)) }).partsBaked;
  m.silhouette.visible = true;
  n += bakeStaticParts(m.silhouette).partsBaked;
  setVehicleTier(m, m.tier);
  return n;
}

export function setVehicleTier(m: VehicleModel, tier: PersonTier): void {
  m.tier = tier;
  m.full.visible = tier === 'full';
  m.silhouette.visible = tier === 'silhouette';
}

// ---------------------------------------------------------------------------
// 63.1e: the damaged and wrecked forms (the carrier's and the transport's)
// ---------------------------------------------------------------------------

function hash01(seed: number, i: number): number {
  const h = Math.sin(seed * 12.9898 + i * 78.233 + 4.1) * 43758.5453;
  return h - Math.floor(h);
}

/** The forms' numbers. */
export const VEHICLE_FORMS = {
  /** the share of its loose parts (skirt panels, stowage, lamps, the aerial, spare links) a form at full level has lost */
  looseShare: 0.6,
  /** scorch on the hull's flat sides — 1 + this × the level patches a side — and where those sides show (person units: the carrier's above its fenders; the transport's from its wheels to its chamfer) */
  sideScorch: 3,
  sides: { tracked: { x: 1.0, y: [1.05, 1.28], z: [-2.5, 1.4] }, wheeled: { x: 1.15, y: [1.15, 1.95], z: [-3.0, 2.3] }, technical: { x: 0.875, y: [0.9, 1.3], z: [-2.3, 2.4] }, supplyTruck: { x: 0.875, y: [0.9, 1.3], z: [-2.3, 2.4] } } as Record<'tracked' | 'wheeled' | 'technical' | 'supplyTruck', { x: number; y: [number, number]; z: [number, number] }>,
  /** the carrier's wreck: its turret blown off its ring — shoved (person units), turned and tipped (radians) — and its tube dropped */
  turretShove: [0.3, 0.12, -0.2] as const, turretTurn: [0.08, 0.55, -0.18] as const, tubeDrop: 0.12,
  /** its thrown track: this many links lying out behind it, over this run (person units) */
  thrownLinks: 18, thrownRun: 3.2,
  /** the transport's wreck: its hull down on the lost front wheel's corner (nose down, rolled, sunk), its ramp hanging, its gun drooped, its cupola turned */
  hullPitch: 0.05, hullRoll: 0.045, hullSink: 0.06, ramp: 0.42, gunDroop: 0.35, cupolaTurn: 0.7,
};

/**
 * 63.1e: the damaged form, in place on a freshly built tracked carrier or transport before it is baked. A share of its
 * loose parts are gone with the level (skirt panels, stowage, lamps, the aerial, spare links). Then 63.4's pass (scorch,
 * the smallest parts knocked off) runs over the hull and the turret each, so the turret's scorch turns with it. The
 * seats' anchors, the gunner's grips, the muzzle, the ramp and the running gear stay. Deterministic in (model, seed,
 * level). Returns how many parts it took off.
 */
export function damageVehicleModel(m: VehicleModel, seed: number, level: number = DAMAGE_FORM.level): number {
  const k = Math.max(0, Math.min(1, level));
  const parts: THREE.Object3D[] = [];
  m.full.traverse(o => { if (o.userData.loose) parts.push(o); });
  const order = parts.map((o, i) => ({ o, h: hash01(seed, i) })).sort((a, b) => a.h - b.h);
  const off = Math.floor(order.length * VEHICLE_FORMS.looseShare * k);
  for (let i = 0; i < off; i++) order[i].o.parent?.remove(order[i].o);
  const grips = Object.values(m.hands).flatMap(h => (h ? [h.right.parent!, h.left.parent!] : []));
  // 63.4's pass scorches a part's box faces: right for the boxes, but a shaped part's box face is not its surface (a
  // patch would float off the 6×6's sloped nose), so the hull's body and the turret's hexagon sit it out ...
  const body = (g: THREE.Object3D) => g.children.filter(c => c.name === 'body');
  let n = off + damageModel(m.hull, seed, k, [m.turret, m.anim.ramp, ...body(m.hull)].filter((o): o is THREE.Object3D => !!o));
  if (m.turret) n += damageModel(m.turret, seed + 1, k, [...(m.cradle ? [m.cradle] : []), ...grips, ...body(m.turret)]);
  // ... and the hull's body is scorched on its flat sides
  const side = m.sides ?? VEHICLE_FORMS.sides[m.kind as 'tracked' | 'wheeled' | 'technical' | 'supplyTruck'];
  if (side) {
    const per = 1 + Math.round(VEHICLE_FORMS.sideScorch * k);
    for (let i = 0; i < 2 * per; i++) {
      const sx = i % 2 ? 1 : -1, len = 0.5 + 0.4 * hash01(seed, 40 + i), tall = 0.18 + 0.2 * hash01(seed, 60 + i);
      const y = side.y[0] + tall / 2 + (side.y[1] - side.y[0] - tall) * hash01(seed, 80 + i);
      const z = side.z[0] + len / 2 + (side.z[1] - side.z[0] - len) * hash01(seed, 100 + i);
      box(m.hull, 0.02, Math.min(tall, side.y[1] - side.y[0]), len, DAMAGE_FORM.scorch, sx * (side.x + 0.011), y, z);
    }
  }
  return n;
}

/**
 * 63.1e: the wreck of a tracked carrier or a transport. It starts as the damaged form at full level, in `p` (a host
 * passes a scorched palette, as the HMG's and the structures' wrecks take), then breaks:
 * - the carrier: its turret blown off its ring and tipped on the deck, its tube dropped, its right track thrown and
 *   lying out behind it with two road wheels gone;
 * - the transport: its front left wheel gone, the hull down on that corner, its ramp hanging, its gun drooped, a burn
 *   through its roof.
 * Baked, and drawn as built: not updated (an update would set the turret's yaw and the ramp back). Deterministic in its
 * arguments.
 */
export function buildVehicleWreck(kind: 'tracked' | 'wheeled' | 'technical' | 'supplyTruck', p: UnitPalette, personHeight: number, seed: number, opts: { technical?: 'standIn' | 'model'; build?: (kind: 'tracked' | 'wheeled' | 'technical' | 'supplyTruck', p: UnitPalette, personHeight: number) => VehicleModel } = {}): VehicleModel {
  const m = opts.build ? opts.build(kind, p, personHeight) : buildVehicleModel(kind, p, personHeight, 'gas', opts);   // (67.60: `build` the D look's)   // (63.1o: lane E's technical's wreck with its model)
  damageVehicleModel(m, seed, 1);
  const W = VEHICLE_FORMS;
  let thrown: THREE.Group | null = null;
  if (kind === 'tracked') {
    const t = m.turret!;
    t.position.x += W.turretShove[0]; t.position.y += W.turretShove[1]; t.position.z += W.turretShove[2];
    t.rotation.set(W.turretTurn[0], W.turretTurn[1], W.turretTurn[2]);
    m.cradle!.rotation.x = W.tubeDrop;
    const loop = m.anim.tracks.find(tr => tr.x > 0)!;
    loop.mesh.parent?.remove(loop.mesh);
    m.anim.tracks = m.anim.tracks.filter(tr => tr !== loop);
    const road = m.anim.wheels.filter(w => w.position.x > 0 && w.position.y < 0.4);
    for (const w of [road[1], road[3]]) { w.parent?.remove(w); m.anim.wheels = m.anim.wheels.filter(x => x !== w); }
    thrown = group(m.full);
    for (let i = 0; i < W.thrownLinks; i++) {
      const s = i / (W.thrownLinks - 1);
      box(thrown, 0.56, 0.06, 0.21, p.dark, 1.25 + 0.8 * s * s, 0.03, -2.4 - s * W.thrownRun).rotation.y = 0.5 * s;   // the run curling away
    }
  } else if (kind === 'technical') {
    // 63.1o: the technical's — its front left wheel gone, down on that corner, its gun drooped and its mount turned, burnt in its bed
    const steer = m.anim.steer.find(g => g.position.x < 0)!, w = steer.children[0] as THREE.Group;
    steer.remove(w);
    m.anim.wheels = m.anim.wheels.filter(x => x !== w);
    m.hull.rotation.set(W.hullPitch, 0, W.hullRoll);
    m.hull.position.y = -W.hullSink;
    m.turret!.rotation.y = W.cupolaTurn;
    m.cradle!.rotation.x = W.gunDroop;
    box(m.hull, 0.8, 0.02, 0.9, '#1a1716', 0, TECHNICAL.bedFloor + 0.05, -1.5);
  } else if (kind === 'supplyTruck') {
    // 166: its front left wheel gone, down on that corner, the tarp burnt through, and two crates thrown off beside it
    const steer = m.anim.steer.find(g => g.position.x < 0)!, w = steer.children[0] as THREE.Group;
    steer.remove(w);
    m.anim.wheels = m.anim.wheels.filter(x => x !== w);
    m.hull.rotation.set(W.hullPitch, 0, W.hullRoll);
    m.hull.position.y = -W.hullSink;
    const L = SUPPLY_TRUCK_MODEL;
    box(m.hull, 0.9, 0.02, 1.0, '#1a1716', 0.15, L.loadTop + 0.06, -0.8);
    thrown = group(m.full);
    box(thrown, L.crate[0], L.crate[1], L.crate[2], p.body, 1.35, L.crate[1] / 2, -1.9).rotation.set(0, 0.6, 0.12);
    box(thrown, L.crate[0], L.crate[1], L.crate[2], p.dark, 1.1, L.crate[1] / 2, -2.85).rotation.set(0.5, 1.2, 0);
  } else {
    const steer = m.anim.steer.find(g => g.position.x < 0)!, w = steer.children[0] as THREE.Group;
    steer.remove(w);
    m.anim.wheels = m.anim.wheels.filter(x => x !== w);
    m.hull.rotation.set(W.hullPitch, 0, W.hullRoll);
    m.hull.position.y = -W.hullSink;
    const fit = m.anim.fittings!;
    fit.position.copy(m.hull.position); fit.rotation.copy(m.hull.rotation);
    const flare = fit.getObjectByName(`flare -1 ${steer.position.z}`);
    flare?.parent?.remove(flare);                                              // its arch's flare torn off with it
    m.anim.ramp!.rotation.x = -W.ramp * RAMP_DOWN;
    m.turret!.rotation.y = W.cupolaTurn;
    m.cradle!.rotation.x = W.gunDroop;
    box(m.hull, 0.9, 0.02, 1.1, '#1a1716', -0.2, (m.roofAt ?? roofAt)(-1.6) + 0.01, -1.6);   // burnt through the roof
  }
  bakeVehicleModel(m, { hinges: 'rest' });   // (a wreck's lids and doors as they stood)
  if (thrown) bakeStaticParts(thrown);
  return m;
}
